import { FC, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { useAppSelector } from '@/redux/hooks'

// Helper function to render text with *bold* markdown syntax
const renderTextWithBold = (text: string): ReactNode[] => {
  const parts: ReactNode[] = []
  const pattern = /\*([^*]+)\*/g
  let lastIndex = 0
  let match: RegExpExecArray | null

  while ((match = pattern.exec(text)) !== null) {
    // Add text before the match
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index))
    }

    // Add the bold text
    parts.push(<strong key={match.index}>{match[1]}</strong>)

    lastIndex = match.index + match[0].length
  }

  // Add remaining text
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex))
  }

  return parts.length > 0 ? parts : [text]
}

const processTextWithTooltips = (
  text: string,
  tooltipConfig: Record<string, string>,
  keyPrefix: string = '',
): ReactNode[] => {
  const terms = Object.keys(tooltipConfig).sort((a, b) => b.length - a.length)

  if (terms.length === 0) {
    return [text]
  }

  // Create a regex pattern that matches whole words only
  const pattern = new RegExp(
    `\\b(${terms.map(term => term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})\\b`,
    'g',
  )

  const parts: ReactNode[] = []
  let lastIndex = 0
  let match: RegExpExecArray | null

  while ((match = pattern.exec(text)) !== null) {
    // Add text before the match
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index))
    }

    // Add the matched term with tooltip
    const term = match[1] ?? match[0]

    parts.push(
      <Tooltip delayDuration={0} key={`${keyPrefix}-${match.index}-${term}`}>
        <TooltipTrigger asChild>
          {/* brand-viol (text-primary) was too light to read comfortably against the page
              background — brand-mullbar gives real contrast; its dark:brand-ros counterpart
              keeps it readable against a dark background too. */}
          <span className="cursor-help font-medium text-brand-mullbar dark:text-brand-ros">
            {term}
          </span>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">
          <p>{renderTextWithBold(tooltipConfig[term] ?? '')}</p>
        </TooltipContent>
      </Tooltip>,
    )

    lastIndex = match.index + term.length
  }

  // Add remaining text
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex))
  }

  return parts.length > 0 ? parts : [text]
}

type TextWithTooltipsProps = {
  children: ReactNode
  tooltipConfig?: Record<string, string>
}

// Helper component to wrap text with tooltips - recursively processes all children
export const TextWithTooltips: FC<TextWithTooltipsProps> = ({ children }) => {
  const { t } = useTranslation()
  const featureAcronymTooltips = useAppSelector(state => state.settings.featureAcronymTooltips)

  if (!featureAcronymTooltips) return children

  const tooltipConfig = t('acronyms', { returnObjects: true }) as Record<string, string>

  if (!tooltipConfig) return children

  // Process children recursively
  const processChildren = (child: ReactNode, index: number): ReactNode => {
    // If it's a string, process it for tooltips
    if (typeof child === 'string') {
      const processed = processTextWithTooltips(child, tooltipConfig, `child-${index}`)

      return processed
    }

    // If it's a number or boolean, return as-is
    if (typeof child === 'number' || typeof child === 'boolean') {
      return child
    }

    // If it's null or undefined, return as-is
    if (child === null) {
      return child
    }

    // If it's a React element, we don't modify it (to avoid breaking structure)
    return child
  }

  // Handle array of children
  if (Array.isArray(children)) {
    return <>{children.map((child, index) => processChildren(child, index))}</>
  }

  // Handle single child
  return <>{processChildren(children, 0)}</>
}
